import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { Upload, Globe, CreditCard, Zap, Shield, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useLanguage } from '@/contexts/LanguageContext';

interface VerticalPanelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const VerticalPanelDialog: React.FC<VerticalPanelDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { isAdmin } = useAdminCheck();
  const { toast } = useToast();
  
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [dailyPrice, setDailyPrice] = useState(15);
  const [existingBookings, setExistingBookings] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen && user) {
      fetchDailyPrice();
      fetchExistingBookings();
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (selectedDates.length > 0) {
      calculatePrice();
    } else {
      setTotalPrice(0);
    }
  }, [selectedDates, dailyPrice]);

  const fetchDailyPrice = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'sky_spot_daily_price_cents')
        .maybeSingle();

      if (error) throw error;
      
      const priceInCents = data?.setting_value ? parseInt(String(data.setting_value)) : 1500;
      setDailyPrice(priceInCents / 100);
    } catch (error) {
      console.error('Error fetching daily price:', error);
    }
  };

  const fetchExistingBookings = async () => {
    try {
      const { data, error } = await supabase
        .from('sky_spots')
        .select('start_date, end_date')
        .eq('position', 1)
        .in('status', ['paid', 'active']);

      if (error) throw error;
      setExistingBookings(data || []);
    } catch (error) {
      console.error('Error fetching existing bookings:', error);
    }
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    
    const dateStr = format(date, 'yyyy-MM-dd');
    
    return existingBookings.some(booking => {
      return dateStr >= booking.start_date && dateStr <= booking.end_date;
    });
  };

  const calculatePrice = async () => {
    if (selectedDates.length === 0) {
      setTotalPrice(0);
      return;
    }

    try {
      const days = selectedDates.length;
      
      const { data, error } = await supabase
        .rpc('calculate_sky_spot_price', { p_days: days });

      if (error) throw error;
      setTotalPrice(data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `sky-spot-${Date.now()}.${fileExt}`;
    const filePath = `sky-spots/${fileName}`;

    const { error } = await supabase.storage
      .from('server-images')
      .upload(filePath, file);

    if (error) throw error;

    const { data } = supabase.storage
      .from('server-images')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Type de fichier invalide",
        description: "Veuillez sélectionner une image (JPEG, PNG, GIF, WebP)",
        variant: "destructive",
      });
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: "Fichier trop volumineux",
        description: "L'image ne doit pas dépasser 5MB",
        variant: "destructive",
      });
      return;
    }

    setImageFile(file);
    setImageUrl('');
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setImageUrl(url);
    
    if (url) {
      setImageFile(null);
      setImagePreview(url);
    } else {
      setImagePreview('');
    }
  };

  const handleSubmit = async () => {
    if (selectedDates.length === 0) {
      toast({
        title: "Dates manquantes",
        description: "Veuillez sélectionner au moins un jour",
        variant: "destructive",
      });
      return;
    }

    if (!imageFile && !imageUrl) {
      toast({
        title: "Image manquante",
        description: "Veuillez uploader une image ou saisir une URL d'image",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const finalImageUrl = imageFile ? await uploadImage(imageFile) : imageUrl;

      const sortedDates = selectedDates.sort((a, b) => a.getTime() - b.getTime());
      const startDate = sortedDates[0];
      const endDate = sortedDates[sortedDates.length - 1];

      const { data, error } = await supabase.functions.invoke('create-sky-spot-payment', {
        body: {
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          imageUrl: finalImageUrl,
          websiteUrl: websiteUrl || null,
        },
      });

      if (error) throw error;

      if (data.url) {
        window.open(data.url, '_blank');
        
        toast({
          title: "Redirection vers le paiement",
          description: "Vous allez être redirigé vers Stripe pour finaliser votre réservation",
        });

        onSuccess();
      }
    } catch (error: any) {
      console.error('Error creating sky spot:', error);
      toast({
        title: "Erreur lors de la réservation",
        description: error.message || "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminAdd = async () => {
    if (selectedDates.length === 0) {
      toast({
        title: "Dates manquantes", 
        description: "Veuillez sélectionner au moins un jour",
        variant: "destructive",
      });
      return;
    }

    if (!imageFile && !imageUrl) {
      toast({
        title: "Image manquante",
        description: "Veuillez uploader une image ou saisir une URL d'image",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const finalImageUrl = imageFile ? await uploadImage(imageFile) : imageUrl;

      const sortedDates = selectedDates.sort((a, b) => a.getTime() - b.getTime());
      const startDate = sortedDates[0];
      const endDate = sortedDates[sortedDates.length - 1];

      const { error } = await supabase
        .from('sky_spots')
        .insert({
          server_id: null,
          position: 1,
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
          image_url: finalImageUrl,
          website_url: websiteUrl || null,
          price_per_day: 0,
          total_amount: 0,
          status: 'active',
        });

      if (error) throw error;

      toast({
        title: "Sky Spot créé",
        description: "Le Sky Spot a été ajouté avec succès",
      });

      resetForm();
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating admin sky spot:', error);
      toast({
        title: "Erreur lors de la création",
        description: error.message || "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedDates([]);
    setImageFile(null);
    setImagePreview('');
    setImageUrl('');
    setWebsiteUrl('');
    setTotalPrice(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!user) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gaming-primary/20 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-gaming-primary" />
            </div>
            {t('skySpot.booking.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Calendrier de sélection multiple */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" />
              {t('skySpot.booking.selectDates')}
            </Label>
            <div className="border border-slate-700 rounded-lg p-4 bg-slate-800">
              <Calendar
                mode="multiple"
                selected={selectedDates}
                onSelect={(dates) => setSelectedDates(dates || [])}
                disabled={isDateDisabled}
                className="pointer-events-auto mx-auto"
                classNames={{
                  day_selected: "bg-gaming-primary text-white hover:bg-gaming-primary/80",
                  day: "text-white hover:bg-slate-700 rounded-md",
                }}
              />
              {selectedDates.length > 0 && (
                <div className="mt-4 p-3 bg-slate-700 rounded-lg">
                  <p className="text-sm text-slate-300 mb-2">
                    {t('skySpot.booking.daysSelected')} ({selectedDates.length}) :
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedDates
                      .sort((a, b) => a.getTime() - b.getTime())
                      .map((date, index) => (
                        <span 
                          key={index}
                          className="text-xs bg-gaming-primary/20 text-gaming-primary px-2 py-1 rounded"
                        >
                          {format(date, 'dd/MM', { locale: fr })}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Image */}
          <div className="space-y-2">
            <Label>{t('skySpot.booking.imageLabel')} {t('skySpot.booking.imageFormat')}</Label>
            <div className="flex flex-col gap-3">
              <div>
                <Label htmlFor="image" className="text-sm text-slate-400">{t('skySpot.booking.option1')} {t('skySpot.booking.maxSize')}</Label>
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="cursor-pointer mt-1"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <div className="h-px bg-slate-600 flex-1"></div>
                <span className="text-xs text-slate-500">{t('skySpot.booking.or')}</span>
                <div className="h-px bg-slate-600 flex-1"></div>
              </div>
              
              <div>
                <Label htmlFor="imageUrl" className="text-sm text-slate-400">{t('skySpot.booking.option2')}</Label>
                <Input
                  id="imageUrl"
                  type="url"
                  value={imageUrl}
                  onChange={handleImageUrlChange}
                  placeholder={t('skySpot.booking.urlPlaceholder')}
                  className="mt-1"
                />
              </div>
              
              {imagePreview && (
                <div className="w-40 aspect-[160/600] border border-slate-700 rounded-md overflow-hidden mx-auto">
                  <img 
                    src={imagePreview} 
                    alt={t('skySpot.booking.preview')} 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>

          {/* URL du site web */}
          <div className="space-y-2">
            <Label htmlFor="website">{t('skySpot.booking.websiteLabel')}</Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                id="website"
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder={t('skySpot.booking.websitePlaceholder')}
                className="pl-10"
              />
            </div>
          </div>

          {/* Prix */}
          {totalPrice > 0 && (
            <div className="bg-slate-800/50 p-4 rounded-lg border border-gaming-primary/20">
              <div className="flex justify-between items-center">
                <span className="text-slate-300">{t('skySpot.booking.totalPrice')} :</span>
                <span className="text-2xl font-bold text-gaming-primary">
                  {(totalPrice / 100).toFixed(2)} €
                </span>
              </div>
              <p className="text-sm text-slate-400 mt-1">
                {selectedDates.length} {t('skySpot.booking.days')} × {dailyPrice}€
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              {t('button.cancel')}
            </Button>
            
            {isAdmin && (
              <Button 
                onClick={handleAdminAdd} 
                disabled={isLoading || selectedDates.length === 0 || (!imageFile && !imageUrl)}
                variant="secondary"
                className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                {isLoading ? (
                  t('skySpot.booking.processing')
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    {t('skySpot.booking.addAdmin')}
                  </>
                )}
              </Button>
            )}
            
            <Button 
              onClick={handleSubmit} 
              disabled={isLoading || selectedDates.length === 0 || (!imageFile && !imageUrl)}
              className="flex-1"
            >
              {isLoading ? (
                t('skySpot.booking.processing')
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  {t('skySpot.booking.proceedPayment')}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
